import { useCallback, useEffect, useState } from "react";
import api from "../api";

/**
 * People, roles and the action log (ROADMAP A6, A2, A3).
 *
 * For administrators only -- the server refuses everyone else, so this page
 * hiding itself is courtesy, not security. Everything here changes who can use
 * what, so every change is recorded in the action log by the server and the
 * Activity tab is where to see it.
 */

interface Person {
  id: number;
  agol_username: string;
  full_name: string;
  email: string | null;
  active: boolean;
  roles: string[];
  exceptions: { permission_key: string; allowed: number; reason: string | null }[];
  administrator: boolean;
  last_sign_in_at: string | null;
}

interface Role {
  key: string;
  label: string;
  description: string | null;
  permissions: string[];
  people: number;
}

interface Permission {
  key: string;
  kind: "tool" | "data" | "admin";
  description: string;
}

interface AuditEvent {
  id: number;
  at: string;
  username: string;
  action: string;
  tool_key: string | null;
  entity_type: string | null;
  entity_id: string | null;
  outcome: "ok" | "denied" | "error";
  summary: string | null;
  details: Record<string, unknown> | null;
}

type Tab = "people" | "roles" | "activity";

const when = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "never";

const errorOf = (e: any, fallback: string) => e?.response?.data?.message || fallback;

export default function AdminPage() {
  const [tab, setTab] = useState<Tab>("people");
  const [people, setPeople] = useState<Person[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [enforced, setEnforced] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");

  const apply = (d: any) => {
    if (d.people) setPeople(d.people);
    if (d.roles) setRoles(d.roles);
    if (d.permissions) setPermissions(d.permissions);
    if (typeof d.enforced === "boolean") setEnforced(d.enforced);
  };

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/people");
      apply(r.data);
    } catch (e: any) {
      if (e?.response?.status === 403) setForbidden(true);
      else setError(errorOf(e, "Could not load people."));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /** One change to the server; the answer carries the fresh lists. */
  const change = async (label: string, request: () => Promise<any>) => {
    setBusy(label);
    setError("");
    try {
      const r = await request();
      apply(r.data);
      return true;
    } catch (e: any) {
      setError(errorOf(e, "That did not save."));
      return false;
    } finally {
      setBusy("");
    }
  };

  if (forbidden) {
    return (
      <div className="ui-card p-4 text-[13px] text-ink-soft">
        This page is for administrators.
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-5xl">
      <div>
        <h1 className="text-xl font-extrabold">People and access</h1>
        <p className="text-[13px] text-ink-soft mt-1">
          Who can sign in, and which tools each person can open.{" "}
          {enforced ? (
            <span className="pill bg-brand-tint text-brand-deep">Enforced</span>
          ) : (
            <span className="pill bg-wnv-tint text-wnv">Observing only</span>
          )}{" "}
          {!enforced &&
            "Roles are being watched, not enforced: a tool outside someone's role still opens, and is noted in Activity. Switching a person off does take effect immediately."}
        </p>
      </div>

      {error && <div className="ui-card p-3 text-[13px] text-wnv">{error}</div>}

      <div className="flex gap-1 border-b border-line">
        {(
          [
            ["people", "People"],
            ["roles", "Roles"],
            ["activity", "Activity"],
          ] as [Tab, string][]
        ).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`px-3 py-2 text-[13px] font-semibold -mb-px border-b-2 ${
              tab === key ? "border-brand text-brand-deep" : "border-transparent text-ink-soft"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "people" && (
        <PeopleTab people={people} roles={roles} busy={busy} change={change} />
      )}
      {tab === "roles" && (
        <RolesTab roles={roles} permissions={permissions} busy={busy} change={change} />
      )}
      {tab === "activity" && <ActivityTab />}
    </div>
  );
}

function PeopleTab({
  people,
  roles,
  busy,
  change,
}: {
  people: Person[];
  roles: Role[];
  busy: string;
  change: (label: string, request: () => Promise<any>) => Promise<boolean>;
}) {
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<string[]>([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ username: "", name: "", email: "", roles: [] as string[] });
  const labelOf = (key: string) => roles.find((r) => r.key === key)?.label || key;

  const toggle = (list: string[], key: string) =>
    list.includes(key) ? list.filter((k) => k !== key) : [...list, key];

  const RoleBoxes = ({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) => (
    <div className="flex flex-wrap gap-x-4 gap-y-1">
      {roles.map((r) => (
        <label key={r.key} className="flex items-center gap-1.5 text-[13px]" title={r.description || ""}>
          <input type="checkbox" checked={value.includes(r.key)} onChange={() => onChange(toggle(value, r.key))} />
          {r.label}
        </label>
      ))}
    </div>
  );

  return (
    <div className="space-y-3">
      <div className="ui-card p-0 overflow-hidden">
        <table className="w-full text-[13px]">
          <thead className="bg-paper-2">
            <tr>
              {["Name", "ArcGIS username", "Roles", "Last signed in", ""].map((h) => (
                <th key={h} className="px-3 py-2 text-left font-semibold whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {people.map((p) => (
              <tr key={p.id} className={`border-t border-line align-top ${p.active ? "" : "opacity-60"}`}>
                <td className="px-3 py-2 font-semibold">
                  {p.full_name}
                  {p.administrator && <span className="pill bg-info-tint text-info ml-2">Administrator</span>}
                  {!p.active && <span className="pill bg-paper-2 text-ink-faint ml-2">Switched off</span>}
                </td>
                <td className="px-3 py-2 font-mono text-[12px]">{p.agol_username}</td>
                <td className="px-3 py-2">
                  {editing === p.agol_username ? (
                    <div className="space-y-2">
                      <RoleBoxes value={draft} onChange={setDraft} />
                      <div className="flex gap-2">
                        <button
                          className="btn-primary text-xs !py-1"
                          disabled={!!busy}
                          onClick={async () => {
                            const ok = await change("roles", () =>
                              api.post(`/admin/people/${encodeURIComponent(p.agol_username)}/roles`, { roles: draft })
                            );
                            if (ok) setEditing(null);
                          }}
                        >
                          {busy === "roles" ? "Saving…" : "Save roles"}
                        </button>
                        <button className="btn-ghost text-xs !py-1" onClick={() => setEditing(null)}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : p.administrator ? (
                    <span className="text-ink-soft">Everything</span>
                  ) : p.roles.length ? (
                    <div className="flex flex-wrap gap-1">
                      {p.roles.map((k) => (
                        <span key={k} className="pill bg-brand-tint text-brand-deep">
                          {labelOf(k)}
                        </span>
                      ))}
                      {p.exceptions.length > 0 && (
                        <span className="pill bg-sle-tint text-sle" title={p.exceptions.map((e) => `${e.allowed ? "+" : "−"} ${e.permission_key}: ${e.reason}`).join("\n")}>
                          {p.exceptions.length} exception{p.exceptions.length === 1 ? "" : "s"}
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="text-ink-faint">No role — can sign in, can open nothing once enforced</span>
                  )}
                </td>
                <td className="px-3 py-2 text-ink-soft whitespace-nowrap">{when(p.last_sign_in_at)}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {!p.administrator && editing !== p.agol_username && (
                    <>
                      <button
                        className="btn-ghost text-xs !py-1 mr-1"
                        onClick={() => {
                          setEditing(p.agol_username);
                          setDraft(p.roles);
                        }}
                      >
                        Edit roles
                      </button>
                      <button
                        className="btn-ghost text-xs !py-1"
                        disabled={!!busy}
                        onClick={() => {
                          if (p.active && !window.confirm(`Switch ${p.full_name} off? They are signed out on their next click.`)) return;
                          change("active", () =>
                            api.post(`/admin/people/${encodeURIComponent(p.agol_username)}/active`, { active: !p.active })
                          );
                        }}
                      >
                        {p.active ? "Switch off" : "Switch on"}
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!adding ? (
        <button className="btn-ghost text-sm" onClick={() => setAdding(true)}>
          + Add a person
        </button>
      ) : (
        <div className="ui-card p-4 space-y-3">
          <h3 className="font-extrabold text-[15px]">Add a person</h3>
          <div className="grid gap-2 sm:grid-cols-3">
            {(
              [
                ["name", "Full name", "Jane Doe"],
                ["username", "ArcGIS username", "jdoe_CMAD"],
                ["email", "Email (optional)", ""],
              ] as ["name" | "username" | "email", string, string][]
            ).map(([k, label, ph]) => (
              <label key={k} className="text-[12px] text-ink-soft">
                {label}
                <input
                  value={form[k]}
                  placeholder={ph}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  className="mt-1 w-full px-3 py-2 border border-line-strong rounded-sm2 text-[13px] text-ink"
                />
              </label>
            ))}
          </div>
          <div>
            <div className="text-[12px] text-ink-soft mb-1">Roles</div>
            <RoleBoxes value={form.roles} onChange={(v) => setForm({ ...form, roles: v })} />
          </div>
          <p className="text-[12px] text-ink-faint">
            They must have an ArcGIS account in the District's organisation to sign in at all. The username is the
            one they sign in with.
          </p>
          <div className="flex gap-2">
            <button
              className="btn-primary text-sm !py-1.5"
              disabled={!!busy || !form.username.trim() || !form.name.trim()}
              onClick={async () => {
                const ok = await change("add", () => api.post("/admin/people", form));
                if (ok) {
                  setAdding(false);
                  setForm({ username: "", name: "", email: "", roles: [] });
                }
              }}
            >
              {busy === "add" ? "Adding…" : "Add"}
            </button>
            <button className="btn-ghost text-sm !py-1.5" onClick={() => setAdding(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function RolesTab({
  roles,
  permissions,
  busy,
  change,
}: {
  roles: Role[];
  permissions: Permission[];
  busy: string;
  change: (label: string, request: () => Promise<any>) => Promise<boolean>;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const [draft, setDraft] = useState<string[]>([]);
  const groups: [Permission["kind"], string][] = [
    ["tool", "Tools"],
    ["data", "Records people may see"],
    ["admin", "Administration"],
  ];

  return (
    <div className="space-y-3">
      <p className="text-[13px] text-ink-soft">
        A role is a bundle of permissions. Changing one changes it for everyone who holds it. The Administrator role is
        everything and cannot be edited.
      </p>
      {roles.map((r) => (
        <div key={r.key} className="ui-card p-4">
          <div className="flex flex-wrap items-baseline gap-2">
            <h3 className="font-extrabold text-[15px]">{r.label}</h3>
            <span className="text-[12px] text-ink-faint">
              {r.people} {r.people === 1 ? "person" : "people"} · {r.permissions.length}{" "}
              {r.permissions.length === 1 ? "permission" : "permissions"}
            </span>
            {r.key !== "administrator" && open !== r.key && (
              <button
                className="btn-ghost text-xs !py-1 ml-auto"
                onClick={() => {
                  setOpen(r.key);
                  setDraft(r.permissions);
                }}
              >
                Edit
              </button>
            )}
          </div>
          {r.description && <p className="text-[12px] text-ink-soft mt-0.5">{r.description}</p>}
          {open === r.key ? (
            <div className="mt-3 space-y-3">
              {groups.map(([kind, title]) => (
                <div key={kind}>
                  <div className="eyebrow !pt-0 mb-1">{title}</div>
                  <div className="grid gap-x-4 gap-y-1 sm:grid-cols-2">
                    {permissions
                      .filter((p) => p.kind === kind)
                      .map((p) => (
                        <label key={p.key} className="flex items-start gap-1.5 text-[13px]" title={p.description}>
                          <input
                            type="checkbox"
                            className="mt-1"
                            checked={draft.includes(p.key)}
                            onChange={() =>
                              setDraft(draft.includes(p.key) ? draft.filter((k) => k !== p.key) : [...draft, p.key])
                            }
                          />
                          <span>
                            {p.key.replace(/^(tool|data|admin):/, "")}
                            <span className="block text-[11px] text-ink-faint">{p.description}</span>
                          </span>
                        </label>
                      ))}
                  </div>
                </div>
              ))}
              <div className="flex gap-2">
                <button
                  className="btn-primary text-sm !py-1.5"
                  disabled={!!busy}
                  onClick={async () => {
                    const ok = await change("role", () =>
                      api.post(`/admin/roles/${encodeURIComponent(r.key)}/permissions`, { permissions: draft })
                    );
                    if (ok) setOpen(null);
                  }}
                >
                  {busy === "role" ? "Saving…" : "Save"}
                </button>
                <button className="btn-ghost text-sm !py-1.5" onClick={() => setOpen(null)}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-1 mt-2">
              {r.key === "administrator" ? (
                <span className="pill bg-info-tint text-info">Everything</span>
              ) : (
                r.permissions.map((k) => (
                  <span key={k} className="pill bg-paper-2 text-ink-soft">
                    {k.replace(/^(tool|data|admin):/, "")}
                  </span>
                ))
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function ActivityTab() {
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [user, setUser] = useState("");
  const [action, setAction] = useState("");
  const [outcome, setOutcome] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const PAGE = 200;
  const [more, setMore] = useState(false);

  /** `older` appends the next page after the oldest event on screen, with the
   *  same filters; otherwise it searches afresh. */
  const load = useCallback(async (older = false) => {
    setLoading(true);
    setError("");
    try {
      const q = new URLSearchParams({ limit: String(PAGE) });
      if (user.trim()) q.set("user", user.trim());
      if (action.trim()) q.set("action", action.trim());
      if (outcome) q.set("outcome", outcome);
      if (older && events.length) q.set("before", String(events[events.length - 1].id));
      const r = await api.get(`/admin/audit?${q}`);
      const got: AuditEvent[] = r.data.events || [];
      setEvents(older ? [...events, ...got] : got);
      setMore(got.length === PAGE);
    } catch (e: any) {
      setError(errorOf(e, "Could not read the action log."));
    } finally {
      setLoading(false);
    }
  }, [user, action, outcome, events]);

  useEffect(() => {
    load();
    // Filters apply on Search, not on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const input = "px-3 py-1.5 border border-line-strong rounded-sm2 text-[13px]";
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-end">
        <label className="text-[12px] text-ink-soft">
          Person
          <input value={user} onChange={(e) => setUser(e.target.value)} className={`${input} block mt-1`} placeholder="username" />
        </label>
        <label className="text-[12px] text-ink-soft">
          Action
          <input
            value={action}
            onChange={(e) => setAction(e.target.value)}
            className={`${input} block mt-1`}
            placeholder="spray_notice."
            title="A full action, or a prefix ending in a dot"
          />
        </label>
        <label className="text-[12px] text-ink-soft">
          Outcome
          <select value={outcome} onChange={(e) => setOutcome(e.target.value)} className={`${input} block mt-1`}>
            <option value="">Any</option>
            <option value="ok">Succeeded</option>
            <option value="denied">Denied</option>
            <option value="error">Failed</option>
          </select>
        </label>
        <button onClick={() => load()} disabled={loading} className="btn-primary text-sm !py-1.5">
          {loading ? "Searching…" : "Search"}
        </button>
      </div>
      {error && <div className="text-[13px] text-wnv">{error}</div>}
      <div className="ui-card p-0 overflow-hidden">
        <div className="overflow-auto max-h-[34rem]">
          <table className="w-full text-[13px]">
            <thead className="bg-paper-2 sticky top-0">
              <tr>
                {["When", "Who", "What", "Record", "Result"].map((h) => (
                  <th key={h} className="px-3 py-2 text-left font-semibold whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {events.map((e) => (
                <tr key={e.id} className="border-t border-line align-top">
                  <td className="px-3 py-2 text-ink-soft whitespace-nowrap">{when(e.at)}</td>
                  <td className="px-3 py-2 font-mono text-[12px]">{e.username}</td>
                  <td className="px-3 py-2">
                    <div className="font-semibold">{e.action}</div>
                    {e.summary && <div className="text-[12px] text-ink-soft">{e.summary}</div>}
                  </td>
                  <td className="px-3 py-2 text-[12px] text-ink-soft">
                    {e.entity_type ? `${e.entity_type} ${e.entity_id ?? ""}` : "—"}
                  </td>
                  <td className="px-3 py-2">
                    {e.action === "access.would_deny" ? (
                      // An observation, not a refusal: the request went through. Older entries
                      // of this kind were stored as "denied", so it is judged by the action.
                      <span
                        className="pill bg-sle-tint text-sle"
                        title="Outside this person's roles, but allowed: roles are only being watched, not enforced."
                      >
                        allowed anyway
                      </span>
                    ) : (
                      <span
                        className={`pill ${
                          e.outcome === "ok" ? "bg-brand-tint text-brand-deep" : e.outcome === "denied" ? "bg-wnv-tint text-wnv" : "bg-paper-2 text-ink-soft"
                        }`}
                      >
                        {e.outcome === "ok" ? "ok" : e.outcome === "denied" ? "denied" : "failed"}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {!events.length && !loading && (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-ink-faint">
                    Nothing recorded for that search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {more && (
          <div className="p-3 border-t border-line text-center">
            <button onClick={() => load(true)} disabled={loading} className="btn-ghost text-sm !py-1.5">
              {loading ? "Loading…" : "Show older"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
